import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Check, CheckCircle2, CircleHelp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { PageHero } from '@/components/layout/PageHero';
import { ApiError } from '@/lib/api-client';
import { useSubmitSupportTicket } from '@/features/support/hooks';
import { supportTicketSchema, type SupportTicketForm } from '@/schemas/support-ticket';

export const Route = createFileRoute('/support')({
  component: SupportPage,
});

/** Helper-band tips — generic, always-true, and text-only (no uploader here). */
const TIPS = [
  'Mention the page or feature you were using.',
  'Tell us what you expected — and what actually happened.',
];

/**
 * Help & Support page — the dedicated route that replaced the old
 * SupportTicketDialog. subject + message -> POST /api/support/tickets (via
 * useSubmitSupportTicket). On success the form is swapped for an inline
 * confirmation. A full-width helper band sits above the form and is where
 * FAQs / guides can grow later.
 */
export function SupportPage() {
  const [sent, setSent] = useState(false);
  const submit = useSubmitSupportTicket();
  const form = useForm<SupportTicketForm>({
    resolver: zodResolver(supportTicketSchema),
    defaultValues: { subject: '', message: '' },
    mode: 'onSubmit',
  });

  const onSubmit = async (values: SupportTicketForm) => {
    try {
      await submit.mutateAsync(values);
      toast.success('Thanks — your support request has been sent.');
      setSent(true);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Could not send your request. Please try again.';
      toast.error(message);
    }
  };

  const sendAnother = () => {
    form.reset({ subject: '', message: '' });
    setSent(false);
  };

  return (
    <>
      <PageHero
        title="Help & Support"
        subtitle="Tell us what's going on and our team will get back to you by email."
      />
      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-8 pb-20 space-y-6">
        {/* Helper band — full width. FAQs / guides can grow here later. */}
        <section className="bg-white border border-line rounded-2xl p-6 sm:p-7 shadow-card">
          <h2 className="text-[15px] font-semibold text-ink m-0">What to expect</h2>
          <p className="text-[14px] leading-relaxed text-muted mt-2 mb-0">
            Send us a message and our team will follow up by email as soon as we can —
            every request is read by a real person. A little detail helps us help you faster:
          </p>
          <ul className="list-none p-0 mt-4 mb-0 grid gap-x-6 gap-y-3 sm:grid-cols-2">
            {TIPS.map((tip) => (
              <li key={tip} className="flex gap-2.5 text-[14px] text-muted">
                <Check className="size-4 text-electric shrink-0 mt-0.5" />
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* FAQ handoff — most common questions are already answered there. */}
        <section className="bg-white border border-line rounded-2xl p-6 sm:p-7 shadow-card flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex gap-3">
            <CircleHelp className="size-5 text-electric shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <h2 className="text-[15px] font-semibold text-ink m-0">Check the FAQ first</h2>
              <p className="text-[14px] text-muted mt-1 mb-0">
                Answers to the most common questions about the platform, the AI agents, and how
                your data is handled.
              </p>
            </div>
          </div>
          <Button asChild variant="outline" className="shrink-0 self-start sm:self-auto">
            <Link to="/faq">Browse the FAQ</Link>
          </Button>
        </section>

        {/* Request form (or its confirmation) — full width. */}
        <div className="bg-white border border-line rounded-2xl p-6 sm:p-8 shadow-card">
          {sent ? (
            <div className="text-center py-8">
              <CheckCircle2 className="size-10 text-state-success-text mx-auto" />
              <h2 className="text-lg font-semibold text-ink mt-4 mb-1">Request sent</h2>
              <p className="text-[14px] text-muted max-w-[360px] mx-auto m-0">
                Thanks — we&rsquo;ll get back to you by email as soon as we can.
              </p>
              <div className="mt-6">
                <Button type="button" variant="outline" onClick={sendAnother}>
                  Send another request
                </Button>
              </div>
            </div>
          ) : (
            <form noValidate onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              <div className="space-y-1.5">
                <label
                  htmlFor="support-subject"
                  className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-muted"
                >
                  Subject
                </label>
                <Input id="support-subject" placeholder="Brief summary" {...form.register('subject')} />
                {form.formState.errors.subject?.message && (
                  <p className="text-[13px] text-state-error-text m-0">
                    {form.formState.errors.subject.message}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="support-message"
                  className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-muted"
                >
                  Message
                </label>
                <Textarea
                  id="support-message"
                  rows={10}
                  placeholder="Describe the issue or question"
                  {...form.register('message')}
                />
                {form.formState.errors.message?.message && (
                  <p className="text-[13px] text-state-error-text m-0">
                    {form.formState.errors.message.message}
                  </p>
                )}
              </div>

              <div className="flex justify-end pt-1">
                <Button type="submit" variant="brand" disabled={submit.isPending}>
                  {submit.isPending ? 'Sending…' : 'Send request'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </main>
    </>
  );
}
